// src/utils/cryptoUtils.js (or wherever you prefer to store utilities)

import CryptoJS from "crypto-js";

const ENCRYPTION_KEY = "YOUR_SUPER_SECRET_KEY"; // **IMPORTANT: Replace with a strong, securely managed key**
// For production, this should ideally come from environment variables
// or a secure key management system, NOT hardcoded.

/**
 * Encrypts a string using AES and then makes the Base64 output URL-safe.
 * @param {string} plaintext - The string to encrypt.
 * @param {string} key - The encryption key.
 * @returns {string} The URL-safe, encrypted string.
 */
function encryptStringToUrlSafe(plaintext: any, key: any) {
  if (!plaintext || !key) {
    console.warn("encryptStringToUrlSafe: Plaintext or key is missing.");
    return null; // Or throw an error
  }
  try {
    const encrypted = CryptoJS.AES.encrypt(plaintext, key);
    let base64String = encrypted.toString();

    // Make it URL-safe
    base64String = base64String
      .replace(/\+/g, "-") // Replace + with -
      .replace(/\//g, "_") // Replace / with _
      .replace(/=+$/, ""); // Remove padding =

    return base64String;
  } catch (error) {
    console.error("Error encrypting string:", error);
    return null;
  }
}

/**
 * Decrypts a URL-safe, encrypted string back to its original plaintext.
 * @param {string} urlSafeCiphertext - The URL-safe encrypted string.
 * @param {string} key - The decryption key.
 * @returns {string | null} The decrypted plaintext string, or null if decryption fails.
 */
function decryptUrlSafeToString(urlSafeCiphertext: any, key: any) {
  // ... (console logs for debugging)
  try {
    let base64String = urlSafeCiphertext.replace(/-/g, "+").replace(/_/g, "/");
    // ... (console logs for debugging)
    while (base64String.length % 4) {
      base64String += "=";
    }
    // ... (console logs for debugging)

    const decrypted = CryptoJS.AES.decrypt(base64String, key);
    // ... (console logs for debugging)
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    // ... (console logs for debugging)

    // THIS IS THE CRITICAL MISSING PIECE!
    // You are not returning `decryptedString` from this function.
    // Therefore, `jsonString` in `decryptObject` will always be `undefined`.
    return decryptedString; // <-- Add this line
  } catch (error) {
    console.error("Error during decryption step:", error);
    return null; // Ensure null is returned on error
  }
}

/**
 * Encrypts a JavaScript object by first stringifying it, then encrypting the string,
 * and finally making the output URL-safe.
 * @param {object} dataObject - The JavaScript object to encrypt.
 * @param {string} [key=ENCRYPTION_KEY] - The encryption key. Defaults to ENCRYPTION_KEY.
 * @returns {string | null} The URL-safe, encrypted string representing the object, or null on error.
 */
export function encryptObject(dataObject: any, key = ENCRYPTION_KEY) {
  if (typeof dataObject !== "object" || dataObject === null) {
    console.error("encryptObject: Input is not a valid object.");
    return null;
  }
  try {
    const jsonString = JSON.stringify(dataObject);
    return encryptStringToUrlSafe(jsonString, key);
  } catch (error) {
    console.error("Error stringifying or encrypting object:", error);
    return null;
  }
}

/**
 * Decrypts a URL-safe, encrypted string back into a JavaScript object.
 * @param {string} encryptedString - The URL-safe encrypted string representing the object.
 * @param {string} [key=ENCRYPTION_KEY] - The decryption key. Defaults to ENCRYPTION_KEY.
 * @returns {object | null} The decrypted JavaScript object, or null if decryption or parsing fails.
 */
export function decryptObject(encryptedString: any, key = ENCRYPTION_KEY) {
  if (typeof encryptedString !== "string" || !encryptedString) {
    console.error("decryptObject: Input is not a valid encrypted string.");
    return null;
  }
  try {
    const jsonString = decryptUrlSafeToString(encryptedString, key) as any;
    if (jsonString) {
      return JSON.parse(jsonString);
    }
    return null; // Decryption failed
  } catch (error) {
    console.error("Error parsing or decrypting object:", error);
    return null;
  }
}

// You can also export the string utility functions if needed elsewhere
// export { encryptStringToUrlSafe, decryptUrlSafeToString };
